import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import App from './App'
import './index.css'
import { getSentryConfig, shouldEnableSentry } from './config/sentry'

// Initialize Sentry only if it should be enabled
if (shouldEnableSentry()) {
  Sentry.init({
    ...getSentryConfig(),
    integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    // Log console errors, warnings, and logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["log", "error", "warn"] }),
    // User feedback widget
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      showBranding: false,
      showName: true,
      showEmail: true,
      isNameRequired: false,
      isEmailRequired: true,
      useSentryUser: {
        name: 'fullName',
        email: 'email',
      },
      buttonLabel: 'Report Issue',
      submitButtonLabel: 'Send Report',
      cancelButtonLabel: 'Cancel',
      formTitle: 'Report an Issue',
      messageLabel: 'What happened?',
      messagePlaceholder: 'Please describe the issue you encountered...',
      successMessageText: 'Thank you for your report!',
      themeDark: {
        background: '#1f2937',
        inputBackground: '#374151',
        inputForeground: '#f3f4f6',
        borderColor: '#4b5563',
        formBorderRadius: '0.5rem',
      },
      themeLight: {
        background: '#ffffff',
        inputBackground: '#f9fafb',
        inputForeground: '#111827',
        borderColor: '#e5e7eb',
        formBorderRadius: '0.5rem',
      },
    }),
    ],
    // Add trace propagation targets
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.clockinnout\.com/,
      /^https:\/\/api\.dev\.clockinnout\.com/
    ],
    // Enable logs
    enableLogs: true,
  })
}

// Initialize language settings
const initializeLanguage = () => {
  const savedLanguage = localStorage.getItem('language') || 'en'
  const savedDir = savedLanguage === 'ar' ? 'rtl' : 'ltr'
  
  document.documentElement.lang = savedLanguage
  document.documentElement.dir = savedDir
}

initializeLanguage()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status
          if (status >= 400 && status < 500) {
            return false
          }
        }
        return failureCount < 3
      },
    },
    mutations: {
      onError: (error) => {
        // Log mutation errors to Sentry
        Sentry.captureException(error, {
          tags: { error_source: 'react_query_mutation' }
        })
      }
    }
  }
})

// Use Sentry's enhanced BrowserRouter for better route tracking  
const SentryBrowserRouter = Sentry.withSentryRouting(BrowserRouter)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">An unexpected error occurred. Please try refreshing the page.</p>
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-gray-500">Error details</summary>
              <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">{String(error)}</pre>
            </details>
            <button
              onClick={resetError}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      showDialog
      onError={(error, componentStack, eventId) => {
        // Log additional error context
        Sentry.withScope((scope) => {
          scope.setContext('component_stack', { componentStack })
          scope.setTag('error_boundary', true)
          Sentry.captureException(error, { event_id: eventId })
        })
      }}
    >
      <SentryBrowserRouter>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </SentryBrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)